import { useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PrimeReactProvider, PrimeReactContext, addLocale } from 'primereact/api'

/**
 * PrimeReact yerel ayarı (tek kaynak). PrimeReact bileşenleri (Calendar, Dropdown …) locale'i
 * PrimeReactProvider **context**'inden okur. Ancak PrimeReactProvider `value.locale`'i yalnızca **mount'ta**
 * kendi state'ine alır (sonradan prop değişimini yok sayar); bu yüzden canlı dil değişimini yansıtmak için
 * context'in `setLocale`'i {@link LocaleSync} alt bileşeniyle çağrılır. Böylece takvim ay/gün adları ve açılır
 * liste boş-mesajı ("Uygun seçenek yok") seçili dile **anlık** uyar.
 *
 * PrimeReact varsayılan 'en' locale ile gelir (İngilizce ay/gün adları + "No available options"); Türkçe için
 * 'tr' burada `addLocale` ile tanımlanır. 'en' varsayılana dokunulmaz (addLocale bütün nesneyi değiştirir).
 */
addLocale('tr', {
  firstDayOfWeek: 1,
  dayNames: ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'],
  dayNamesShort: ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'],
  dayNamesMin: ['Pz', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct'],
  monthNames: [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
  ],
  monthNamesShort: ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'],
  today: 'Bugün',
  clear: 'Temizle',
  emptyMessage: 'Uygun seçenek yok',
  emptyFilterMessage: 'Sonuç bulunamadı',
})

/** Uygulama dilini (i18n) PrimeReact context locale'i olarak canlı senkronlar (setLocale). */
function LocaleSync() {
  const { i18n } = useTranslation()
  const ctx = useContext(PrimeReactContext)
  const locale = i18n.language.startsWith('tr') ? 'tr' : 'en'
  useEffect(() => {
    ctx?.setLocale?.(locale)
  }, [locale, ctx])
  return null
}

export default function PrimeLocaleProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { i18n } = useTranslation()
  // Mount anındaki dil doğru başlasın (ilk paint 'en' varsayılanına düşmesin); canlı değişimi LocaleSync yürütür.
  const value = useMemo(
    () => ({ locale: i18n.language.startsWith('tr') ? 'tr' : 'en' }),
    // yalnız mount değeri; sonraki değişim setLocale ile
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  return (
    <PrimeReactProvider value={value}>
      <LocaleSync />
      {children}
    </PrimeReactProvider>
  )
}
